import { FormikProps } from 'formik';
import * as Yup from 'yup';
import { Account } from '../accounts/Accounts';
import { UserAuth } from '../users/User';
import { Food, ResponseFoodsProps } from '../foods/Food';
import { ComplementsStatus } from '../../utils/enums';
import {
  CabysCatalog,
  EconomicActivity,
  LatestCabysCodes,
} from '../settings/Setting';
import {
  GetCabysCatalogArgs,
  GetEconomicActivityRestaurantgArgs,
  LatestCabysCodesArgs,
} from '../../store/actions/settings/SettingTypes';
import { FoodCategory } from '../foodCategories/FoodCategory';
import { FoodComplement } from '../foodComplements/FoodComplement';
import { GetCategoriesProps } from 'src/store/actions/foodCategories/FoodCategoriesTypes';
import { GetListFoodComplementsProps } from 'src/store/actions/foodComplements/FoodComplementsTypes';
import { CABYS_DEFAULT, MAX_PRODUCT_MASSIVE } from '../../utils/constants';

export interface FoodMassiveFormProps {
  formikInstance: FormikProps<FoodsFormValuesMassive>;
  complements: FoodComplement[];
  categories: FoodCategory[];
  cabysCatalog: CabysCatalog[];
  getCabysCatalog: ({ ...args }: GetCabysCatalogArgs) => Promise<void>;
  latestCabysCodes: LatestCabysCodes[];
  getLatestCabysCodes: ({ ...args }: LatestCabysCodesArgs) => Promise<void>;
  user: UserAuth;
  economicActivityRestaurant: EconomicActivity[];
  getEconomicActivityRestaurant: ({
    ...args
  }: GetEconomicActivityRestaurantgArgs) => Promise<void>;
}

export interface FoodsFormProps {
  formikInstance: FormikProps<FoodsFormValues>;
  food?: Food;
  type?: 'crear' | 'editar';
  loadFile: boolean;
  setLoadFile: React.Dispatch<React.SetStateAction<boolean>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  selectedFile: any;
  setSelectedFile: React.Dispatch<React.SetStateAction<any>>;
  complements: { label: string; value: string }[];
  setComplements: React.Dispatch<
    React.SetStateAction<{ label: string; value: string }[]>
  >;
  openComplements: boolean;
  setOpenComplements: React.Dispatch<React.SetStateAction<boolean>>;
  selectedComplements: string[];
  setSelectedComplements: React.Dispatch<React.SetStateAction<string[]>>;
  categories: { label: string; value: string }[];
  setCategories: React.Dispatch<
    React.SetStateAction<{ label: string; value: string }[]>
  >;
  openCategories: boolean;
  setOpenCategories: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCategories: string[];
  setSelectedCategories: React.Dispatch<React.SetStateAction<string[]>>;
  user: UserAuth;
  cabysCatalog: CabysCatalog[];
  getCabysCatalog: ({ ...args }: GetCabysCatalogArgs) => Promise<void>;
  latestCabysCodes: LatestCabysCodes[];
  getLatestCabysCodes: ({ ...args }: LatestCabysCodesArgs) => Promise<void>;
  economicActivityRestaurant: EconomicActivity[];
  getEconomicActivityRestaurant: ({
    user,
    callbackPre,
    callbackSuccess,
    callbackError,
  }: GetEconomicActivityRestaurantgArgs) => Promise<void>;
}

export interface FoodsFormValues {
  id?: string;
  _id?: string;
  name: string;
  description: string;
  status: string;
  price: number;
  account: string;
  discountPercent: number;
  foodCategoryId: Array<string>;
  complements: Array<string>;
  image: string;
  showOnMenu?: string;
  cabys: string;
  ivaPercentage: number;
  economicActivityCode: string;
}

export interface FoodProduct
  extends Omit<FoodsFormValues, 'id' | '_id' | 'image'> {
  image?: any;
  fileImage?: any;
}

export const FoodProductInitialValues: FoodProduct = {
  name: '',
  account: '',
  foodCategoryId: [],
  complements: [],
  description: '',
  discountPercent: 0,
  image: '',
  price: 0,
  status: ComplementsStatus.ACTIVE,
  showOnMenu: 'true',
  cabys: CABYS_DEFAULT,
  ivaPercentage: 0,
  fileImage: null,
  economicActivityCode: '',
};

export interface FoodsFormValuesMassive {
  products: FoodProduct[];
}

export const FoodsFormValuesMassiveInitialValues: FoodsFormValuesMassive = {
  products: [],
};

export const FoodsFormValuesMassiveValidationSchema: Yup.SchemaOf<FoodsFormValuesMassive> =
  Yup.object({
    products: Yup.array()
      .of(
        Yup.object({
          name: Yup.string().required('Este campo es requerido'),
          account: Yup.string(),
          foodCategoryId: Yup.array().required('Este campo es requerido'),
          complements: Yup.array(),
          description: Yup.string().required('Este campo es requerido'),
          discountPercent: Yup.number()
            .typeError('Debe ser un número, separado por puntos. Ej: 0.0')
            .test(
              'Validacion de Descuento',
              'El porcentaje de descuento debe estar entre 0 y 100%',
              (value) => (value < 0 || value > 100 ? false : true)
            )
            .required('Este campo es requerido'),
          image: Yup.string().notRequired().nullable(),
          fileImage: Yup.mixed().notRequired().nullable(),
          price: Yup.number()
            .typeError('Debe ser un número, separado por puntos. Ej: 0.0')
            .required('Este campo es requerido'),
          status: Yup.string().required('Este campo es requerido'),
          showOnMenu: Yup.string().default('true'),
          cabys: Yup.string().required('Este campo es requerido'),
          ivaPercentage: Yup.number()
            .typeError('Debe ser un número, separado por puntos. Ej: 0.0')
            .test(
              'Validacion de IVA',
              'El porcentaje de IVA debe estar entre 0 y 100%',
              (value) => (value < 0 || value > 100 ? false : true)
            )
            .required('Este campo es requerido'),
          economicActivityCode: Yup.string(),
        })
      )
      .min(1, 'Debe agregar al menos un producto')
      .max(
        MAX_PRODUCT_MASSIVE,
        `No puede agregar más de ${MAX_PRODUCT_MASSIVE} productos`
      )
      .required('Este campo es requerido'),
  });

export interface ManagementFoodsCreateProps {
  user: UserAuth;
  account: Account;
  createFood: (
    form: object,
    callbackSuccess: Function,
    callbackError: Function
  ) => Promise<void>;
  getAccount: (accountId: string, callback: Function) => Promise<void>;
  cabysCatalog: CabysCatalog[];
  getCabysCatalog: ({ ...args }: GetCabysCatalogArgs) => Promise<void>;
  latestCabysCodes: LatestCabysCodes[];
  getLatestCabysCodes: ({ ...args }: LatestCabysCodesArgs) => Promise<void>;
  economicActivityRestaurant: EconomicActivity[];
  getEconomicActivityRestaurant: ({
    ...args
  }: GetEconomicActivityRestaurantgArgs) => Promise<void>;
}

export interface FoodsCreateMassiveProps {
  user: UserAuth;
  cabysCatalog: CabysCatalog[];
  getCabysCatalog: ({ ...args }: GetCabysCatalogArgs) => Promise<void>;
  latestCabysCodes: LatestCabysCodes[];
  getLatestCabysCodes: ({ ...args }: LatestCabysCodesArgs) => Promise<void>;
  categories: FoodCategory[];
  complements: FoodComplement[];
  getCategories: ({ ...args }: GetCategoriesProps) => Promise<void>;
  getComplements: ({ ...args }: GetListFoodComplementsProps) => Promise<void>;
  economicActivityRestaurant: EconomicActivity[];
  getEconomicActivityRestaurant: ({
    ...args
  }: GetEconomicActivityRestaurantgArgs) => Promise<void>;
}

export interface ManagementFoodsEditProps {
  user: UserAuth;
  food: Food;
  updateFood: (
    form: object,
    callbackSuccess: Function,
    callbackError: Function
  ) => Promise<void>;
  getFood: (id: string, callback: Function) => Promise<void>;
  cabysCatalog: CabysCatalog[];
  getCabysCatalog: ({ ...args }: GetCabysCatalogArgs) => Promise<void>;
  latestCabysCodes: LatestCabysCodes[];
  getLatestCabysCodes: ({ ...args }: LatestCabysCodesArgs) => Promise<void>;
  economicActivityRestaurant: EconomicActivity[];
  getEconomicActivityRestaurant: ({
    ...args
  }: GetEconomicActivityRestaurantgArgs) => Promise<void>;
}

export interface ManagementFoodsProps {
  user: UserAuth;
  account: Account;
  foods: ResponseFoodsProps;
  getFoods: (
    page: number,
    limit: number,
    accountId: string,
    search: string | null
  ) => Promise<void>;
  deleteFood: (
    accountId: string,
    id: object,
    callbackSuccess: Function,
    callbackError: Function
  ) => Promise<void>;
  getAccount: (accountId: string, callback: Function) => Promise<void>;
}

export const initialValues: FoodsFormValues = {
  id: '',
  _id: '',
  name: '',
  description: '',
  status: ComplementsStatus.ACTIVE,
  price: 0,
  account: '',
  discountPercent: 0,
  foodCategoryId: [],
  complements: [],
  image: '',
  showOnMenu: 'true',
  cabys: CABYS_DEFAULT,
  ivaPercentage: 0,
  economicActivityCode: '',
};

export const validationSchema: Yup.SchemaOf<FoodsFormValues> = Yup.object({
  _id: Yup.string(),
  id: Yup.string(),
  name: Yup.string().required('Este campo es requerido'),
  description: Yup.string().required('Este campo es requerido'),
  status: Yup.string().required('Este campo es requerido'),
  price: Yup.number()
    .positive('La cantidad debe ser positiva')
    .typeError('Este campo debe ser un número')
    .required('Este campo es requerido'),
  account: Yup.string().required('Este campo es requerido'),
  discountPercent: Yup.number()
    .typeError('Debe ser un número, separado por puntos. Ej: 0.0')
    .test(
      'Evaluando descuento',
      'El porcentaje de descuento debe estar entre 0 y 100%',
      (price) => (price < 0 || price > 100 ? false : true)
    )
    .required('Este campo es requerido'),
  foodCategoryId: Yup.array().required('Este campo es requerido'),
  complements: Yup.array(),
  image: Yup.string(),
  showOnMenu: Yup.string().default('true'),
  cabys: Yup.string().required('Este campo es requerido'),
  ivaPercentage: Yup.number()
    .typeError('Debe ser un número, separado por puntos. Ej: 0.0')
    .test(
      'Evaluando iva',
      'El porcentaje de iva debe estar entre 0 y 100%',
      (price) => (price < 0 || price > 100 ? false : true)
    )
    .required('Este campo es requerido'),
  economicActivityCode: Yup.string(),
});

export const foodInitialState: Food = {
  _id: '',
  account: '',
  complements: [],
  createdAt: '',
  createdBy: '',
  deletedAt: null,
  description: '',
  discountPercent: 0,
  foodCategoryId: [],
  image: '',
  name: '',
  price: 0,
  status: '',
  timeStampId: 0,
  quantity: 0,
  quantityToPay: 0,
  serviceFee: 0,
  servicePercentage: 0,
  vatFee: 0,
  vatPercentage: 0,
  orderStatus: '',
  otherFee: 0,
  otherPercentage: 0,
  discountFee: 0,
  otherPercentageToPay: 0,
  servicePercentageToPay: 0,
  vatPercentageToPay: 0,
  otherFeeToPay: 0,
  vatFeeToPay: 0,
  discountFeeToPay: 0,
  subTotalToPay: 0,
  serviceFeeToPay: 0,
  totalToPay: 0,
  cabys: '',
  ivaPercentage: 0,
  economicActivityCode: '',
};
